import React from 'react';

import Modal from 'ui-library/modals/Modal';

interface RangeFeature {
  name: string;
  start: number;
  end: number;
  length: number | null;
}

export interface ConversionWarningProps {
  annotations: Array<RangeFeature>;
  onRequestClose: () => void;
  onRequestSubmit: (accept: boolean) => void;
  pairs: Array<{forwardName: string; forwardLocation: string; reverseName: string; reverseLocation: string}>;
  parts: Array<RangeFeature>;
  translations: Array<RangeFeature>;
}

const RangeFeatureTable = ({
  features,
  includeLength = true,
}: {
  features: Array<RangeFeature>;
  includeLength?: boolean;
}): JSX.Element => (
  <div className="scroll">
    <table className="table table-condensed">
      <thead>
        <tr>
          <th>Name</th>
          <th>Start</th>
          <th>End</th>
          {includeLength && <th>Length</th>}
        </tr>
      </thead>
      <tbody>
        {features.map((feature, i) => (
          <tr key={i}>
            <td>{feature.name}</td>
            <td>{feature.start}</td>
            <td>{feature.end}</td>
            {includeLength && <td>{feature.length}</td>}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export function ConversionWarningModal({
  annotations,
  onRequestClose,
  onRequestSubmit,
  pairs,
  parts,
  translations,
}: ConversionWarningProps): JSX.Element {
  const numAllFeatures = annotations.length + pairs.length + parts.length + translations.length;
  return (
    <Modal
      buttons={[
        {text: 'Cancel', cancel: true},
        {text: 'OK', submit: true},
      ]}
      onRequestClose={onRequestClose}
      onRequestSubmit={() => {
        onRequestSubmit(true);
      }}
      title="Conversion Warning"
    >
      {numAllFeatures > 0 && (
        <p>
          In converting the sequence from circular to linear, the following features will be lost. If you wish
          to keep these features on your sequence, you can try reindexing the sequence so that they do not
          cross the origin.
        </p>
      )}
      {annotations.length > 0 && (
        <>
          <p>Annotations</p>
          <RangeFeatureTable features={annotations} />
        </>
      )}
      {translations.length > 0 && (
        <>
          <p>Translations (all regions of the following translations will be removed)</p>
          <RangeFeatureTable features={translations} includeLength={false} />
        </>
      )}
      {parts.length > 0 && (
        <>
          <p>Parts</p>
          <RangeFeatureTable features={parts} />
        </>
      )}
      {pairs.length > 0 && (
        <>
          <p>Primer pairs (pairs will be unlinked but individual primers will be preserved)</p>
          <div className="scroll">
            <table className="table table-condensed">
              <thead>
                <tr>
                  <th>Forward Primer</th>
                  <th />
                  <th>Reverse Primer</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {pairs.map((pair, i) => (
                  <tr key={i}>
                    <td>{pair.forwardName}</td>
                    <td>{pair.forwardLocation}</td>
                    <td>{pair.reverseName}</td>
                    <td>{pair.reverseLocation}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      <p>Proceed with the conversion anyway?</p>
    </Modal>
  );
}
